/**
 * Every page that loads on the way in, in one place: the router draws them (`app.tsx`) and the prefetcher loads
 * them ahead of the click (`lib/route-prefetch.ts`), so the two can never name different chunks.
 *
 * The leaderboard is the first page for most people and stays in the main bundle. Everything else is its own
 * chunk, for the reasons written beside each.
 */
import { catalogPartsReady } from "@/i18n";
import { lazyRoute } from "@/lib/lazy-route";

/**
 * A page's code together with the catalog sections that load after the first paint (`i18n/catalogs.ts`), so no
 * page draws before its strings are in. Both start at boot, so this rarely waits.
 */
const withStrings =
  <M>(load: () => Promise<M>) =>
  (): Promise<M> =>
    Promise.all([load(), catalogPartsReady]).then(([module]) => module);

// The pages the leaderboard links to carry the full charts (recharts).
export const EditorDetailPage = lazyRoute(withStrings(() => import("@/pages/editor-detail")), (m) => m.EditorDetailPage);
export const CreativeDetailPage = lazyRoute(withStrings(() => import("@/pages/creative-detail")), (m) => m.CreativeDetailPage);
export const TopVideosPage = lazyRoute(withStrings(() => import("@/pages/top-videos")), (m) => m.TopVideosPage);

// Every setting on the platform, opened now and then (the sidebar's Settings, the menu under the person's name);
// each section's code is its own chunk on top of the hub's.
export const SettingsPage = lazyRoute(withStrings(() => import("@/features/settings/settings-page")), (m) => m.SettingsPage);
export const UsersPage = lazyRoute(withStrings(() => import("@/features/users/users-page")), (m) => m.UsersPage);
// Opened when a machine credential is minted or rotated.
export const ApiKeysPage = lazyRoute(withStrings(() => import("@/features/api-keys/api-keys-page")), (m) => m.ApiKeysPage);

// The pipeline is the heaviest feature in the app and the one an editor opens first.
export const AssignmentsPage = lazyRoute(
  withStrings(() => import("@/features/pipeline/editor/assignments-page")),
  (m) => m.AssignmentsPage,
);
export const ConceptPage = lazyRoute(withStrings(() => import("@/features/pipeline/editor/concept-page")), (m) => m.ConceptPage);
// Every concept of every editor: a strategist's whole pipeline, and an admin's second view of it.
export const ConceptsOverviewPage = lazyRoute(
  withStrings(() => import("@/features/pipeline/overview/concepts-overview-page")),
  (m) => m.ConceptsOverviewPage,
);
// Every published deck, report and showcase, and one of them in the in-app viewer: an admin's.
export const ArtifactsPage = lazyRoute(withStrings(() => import("@/features/master/artifacts/artifacts-page")), (m) => m.ArtifactsPage);
export const ArtifactViewerPage = lazyRoute(withStrings(() => import("@/features/master/artifacts/artifact-viewer-page")), (m) => m.ArtifactViewerPage);
// The admins' Master AI: the studio's chat kit and the batch cards, opened by a few people now and then.
export const MasterPage = lazyRoute(withStrings(() => import("@/features/master/chat/master-page")), (m) => m.MasterPage);
// Who is on the platform and what they did: an admin's page.
export const ActivityPage = lazyRoute(withStrings(() => import("@/features/activity/activity-page")), (m) => m.ActivityPage);
// AI generation's team library: product pictures and voices.
export const LibraryPage = lazyRoute(withStrings(() => import("@/features/generation/library/library-page")), (m) => m.LibraryPage);
// The prompt chat's team memory, change log and settings.
export const PromptChatPage = lazyRoute(
  withStrings(() => import("@/features/prompt-chat/memory/memory-page")),
  (m) => m.PromptChatPage,
);
// AI generation's spend and settings: an admin's page.
export const SpendPage = lazyRoute(withStrings(() => import("@/features/generation/admin/spend-page")), (m) => m.SpendPage);
// Every dubbed and subtitled ad, by the creative it translates. An admin's page.
export const TranslationsPage = lazyRoute(
  withStrings(() => import("@/features/translations/translations-page")),
  (m) => m.TranslationsPage,
);
export const ResourcesPage = lazyRoute(withStrings(() => import("@/features/resources/resources-page")), (m) => m.ResourcesPage);
// Ideas, "suggest a concept": the list and one idea, under one address.
export const IdeasRoutes = lazyRoute(withStrings(() => import("@/features/ideas/ideas-page")), (m) => m.IdeasRoutes);

// The presentation editor: the summary (with the script's edit mode), one line, the deck the editors work from.
// Each its own chunk: the editor is opened by a handful of people, the deck by every editor.
export const PresentationSummaryPage = lazyRoute(
  withStrings(() => import("@/features/storyboard/summary/summary-page")),
  (m) => m.PresentationSummaryPage,
);
export const LinePage = lazyRoute(withStrings(() => import("@/features/storyboard/line/line-page")), (m) => m.LinePage);
export const ShotRedirect = lazyRoute(withStrings(() => import("@/features/storyboard/line/shot-redirect")), (m) => m.ShotRedirect);
export const DeckPage = lazyRoute(withStrings(() => import("@/features/storyboard/deck/deck-page")), (m) => m.DeckPage);
// The editor's view of the same addresses: one line at a time, Video and Frames, Edit and Download.
export const EditorLinePage = lazyRoute(
  withStrings(() => import("@/features/storyboard/editor-view/editor-line-page")),
  (m) => m.EditorLinePage,
);

// The clip editor: full-window, desktop only, opened by the few people working a concept's video.
export const ClipEditorPage = lazyRoute(withStrings(() => import("@/features/clip-editor/clip-editor-page")), (m) => m.ClipEditorPage);

// The coach carries a markdown renderer, a syntax highlighter and a diagram engine.
export const AgentChatPage = lazyRoute(withStrings(() => import("@/features/agent-chat/chat")), (m) => m.AgentChatPage);
export const AgentSessionsPage = lazyRoute(withStrings(() => import("@/features/agent-chat/sessions")), (m) => m.AgentSessionsPage);

// The studio: the scriptwriter's chats, the ad jobs they hand on, and the cloud agents' admin page.
export const StudioLayout = lazyRoute(withStrings(() => import("@/features/studio/shell/studio-layout")), (m) => m.StudioLayout);
export const StudioHome = lazyRoute(withStrings(() => import("@/features/studio/home/studio-home")), (m) => m.StudioHome);
export const StudioChatPage = lazyRoute(withStrings(() => import("@/features/studio/shell/chat-page")), (m) => m.ChatPage);
export const StudioJobsPage = lazyRoute(withStrings(() => import("@/features/studio/jobs/list/jobs-page")), (m) => m.JobsPage);
export const StudioNewJobPage = lazyRoute(withStrings(() => import("@/features/studio/jobs/new/new-job-page")), (m) => m.NewJobPage);
export const StudioJobPage = lazyRoute(withStrings(() => import("@/features/studio/jobs/detail/job-page")), (m) => m.JobPage);
export const StudioJobPresentationPage = lazyRoute(
  withStrings(() => import("@/features/studio/jobs/detail/job-presentation-page")),
  (m) => m.JobPresentationPage,
);
export const AgentsAdminPage = lazyRoute(
  withStrings(() => import("@/features/studio/admin/agents-admin-page")),
  (m) => m.AgentsAdminPage,
);
